//frontend/src/admin/CargoRequestsManagement.js
import React, { useState, useEffect } from 'react';
import { 
  FaBoxOpen, FaUser, FaPhone, FaWeight, FaCube, FaImage,
  FaFileAlt, FaClock, FaCheckCircle, FaTimes, FaEye, FaFilter,
  FaTrash, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../axios';
import '../styles/admin.css';

const CargoRequestsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const notificationId = searchParams.get('notification_id');
  const cargoId = searchParams.get('cargo_id');

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  const [filters, setFilters] = useState({
    status: 'all',
    page: 1,
    page_size: 6 // 🆕 Default 6
  });
  
  const [pagination, setPagination] = useState({
    total_pages: 0,
    total_items: 0,
    current_page: 1
  });

  // Статистик
  useEffect(() => {
    fetchStats();
  }, []);

  // Жагсаалт
  useEffect(() => {
    fetchRequests();
  }, [filters]);

  // URL-аас notification ID ирвэл нээх
  useEffect(() => {
    const handleNotificationRedirect = async () => {
      if (cargoId) {
        // console.log('🔍 Opening cargo request:', cargoId);
        try {
          await fetchRequestDetail(cargoId);
          if (notificationId) {
            setTimeout(() => {
              setSearchParams({});
            }, 500);
          }
        } catch (error) {
          console.error('Failed to open cargo request:', error);
        }
      }
    };
    
    handleNotificationRedirect();
  }, [cargoId, notificationId]);

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get('/api/client/cargo-requests/stats');
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/client/cargo-requests', { params: filters });
      if (res.data.success) {
        setRequests(res.data.data || []);
        setPagination({
          total_pages: res.data.total_pages,
          total_items: res.data.total_items,
          current_page: res.data.current_page
        });
      }
    } catch (error) {
      console.error('Fetch requests error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestDetail = async (id) => {
    try {
      const res = await axiosInstance.get(`/api/client/cargo-requests/${id}`);
      if (res.data.success) {
        setSelectedRequest(res.data.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Fetch detail error:', error);
      alert('Дэлгэрэнгүй мэдээлэл татахад алдаа гарлаа');
    }
  };

  const handleStatusChange = async (id, newStatus, adminNotes = '') => {
    try {
      await axiosInstance.patch(`/api/client/cargo-requests/${id}/status`, {
        status: newStatus,
        admin_notes: adminNotes
      });
      
      alert('✅ Төлөв амжилттай өөрчлөгдлөө');
      fetchRequests();
      fetchStats();
      
      if (selectedRequest && selectedRequest.id === id) {
        fetchRequestDetail(id);
      }
    } catch (error) {
      console.error('Status change error:', error);
      alert('❌ ' + (error.response?.data?.msg || 'Алдаа гарлаа'));
    }
  };

  // 🆕 Устгах үйлдэл
    const handleDelete = async (id) => {
      if (!window.confirm('⚠️ Энэ ачаа дуудлагыг устгах уу?')) {
        return;
      }
      
      try {
        // ✅ Ачаа устгах
        await axiosInstance.delete(`/api/client/cargo-requests/${id}`);
        
        // ✅ Холбогдох notification-ийг устгах
        try {
          await axiosInstance.delete(`/api/notifications/by-cargo/${id}`);
        } catch (notifError) {
          console.warn('Delete cargo notification warning:', notifError);
          // Notification устгахад алдаа гарсан ч үргэлжлүүлнэ
        }
        
        alert('✅ Ачаа дуудлага устгагдлаа');
        fetchRequests();
        fetchStats();
        
        if (showDetailModal) {
          setShowDetailModal(false);
          setSelectedRequest(null);
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('❌ ' + (error.response?.data?.msg || 'Устгахад алдаа гарлаа'));
      }
    };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'Хүлээгдэж буй', class: 'badge badge-warning' },
      contacted: { text: 'Холбогдсон', class: 'badge badge-info' },
      processed: { text: 'Боловсруулсан', class: 'badge badge-success' },
      cancelled: { text: 'Цуцлагдсан', class: 'badge badge-secondary' }
    };
    return statusMap[status] || statusMap.pending;
  };

  const getMeasurementLabel = (type) => {
    return type === 'weight' ? 'Жингээр' : 'Эзэлхүүнээр';
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Дөнгөж сая';
    if (diffMins < 60) return `${diffMins} минутын өмнө`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} цагийн өмнө`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} өдрийн өмнө`;
  };

  // 🆕 Хуудас өөрчлөх
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setFilters({ ...filters, page: newPage });
    }
  };

  // 🆕 Page size өөрчлөх
  const handlePageSizeChange = (newSize) => {
    setFilters({ ...filters, page_size: newSize, page: 1 });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <FaBoxOpen className="mr-3 text-blue-600" />
          Ачаа дуудлага удирдлага
        </h1>
        <p className="text-sm text-gray-600">
          Хэрэглэгчдийн ачаа дуудлагын хүсэлтүүд
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-gray-600 text-sm mb-1">Нийт</div>
          <div className="text-2xl font-bold text-gray-800">{stats.total || 0}</div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4">
          <div className="text-yellow-700 text-sm mb-1">Хүлээгдэж буй</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.pending || 0}</div>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-4">
          <div className="text-blue-700 text-sm mb-1">Холбогдсон</div>
          <div className="text-2xl font-bold text-blue-600">{stats.contacted || 0}</div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4">
          <div className="text-green-700 text-sm mb-1">Боловсруулсан</div>
          <div className="text-2xl font-bold text-green-600">{stats.processed || 0}</div>
        </div>
        <div className="bg-purple-50 rounded-lg shadow p-4">
          <div className="text-purple-700 text-sm mb-1">Өнөөдөр</div>
          <div className="text-2xl font-bold text-purple-600">{stats.today || 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <FaFilter className="text-gray-500" />
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-gray-700">Төлөв:</label>
            <select
              className="custom-input"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="all">Бүгд</option>
              <option value="pending">Хүлээгдэж буй</option>
              <option value="contacted">Холбогдсон</option>
              <option value="processed">Боловсруулсан</option>
              <option value="cancelled">Цуцлагдсан</option>
            </select>
          </div>

          {/* 🆕 Page Size сонголт */}
          <div className="flex items-center gap-2 ml-auto">
            <label className="text-sm font-semibold text-gray-700">Хуудсанд:</label>
            <select
              className="custom-input"
              value={filters.page_size}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            >
              <option value="6">6 дуудлага</option>
              <option value="9">9 дуудлага</option>
              <option value="18">18 дуудлага</option>
              <option value="27">27 дуудлага</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="loading-spinner"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <FaBoxOpen className="mx-auto text-6xl text-gray-300 mb-4" />
            <p className="text-gray-500">Ачаа дуудлага байхгүй</p>
          </div>
        ) : (
          requests.map((req) => (
            <div key={req.id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-5">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    {getTimeAgo(req.created_at)}
                  </div>
                  <h3 className="font-bold text-lg text-gray-800 mb-1">
                    {req.sender_name}
                  </h3>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <FaPhone className="text-xs" />
                    {req.sender_phone}
                  </p>
                </div>
                <span className={getStatusBadge(req.status).class}>
                  {getStatusBadge(req.status).text}
                </span>
              </div>

              {/* Info */}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Код:</span>
                  <span className="font-mono font-semibold text-blue-600">{req.request_code}</span>
                </div>
                
                {req.cargo_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Ачаа:</span>
                    <span className="font-semibold">{req.cargo_name}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Хэмжээ:</span>
                  <span className="font-semibold">
                    {req.measurement_type === 'weight' 
                      ? `${req.weight_kg} кг`
                      : `${req.volume_cbm} м³`
                    }
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Огноо:</span>
                  <span className="text-gray-800">
                    {new Date(req.created_at).toLocaleDateString('mn-MN')}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <button
                onClick={() => fetchRequestDetail(req.id)}
                className="w-full custom-button text-sm py-2 flex items-center justify-center gap-2"
              >
                <FaEye />
                Дэлгэрэнгүй харах
              </button>
            </div>
          ))
        )}
      </div>

      {/* 🆕 Pagination */}
      {pagination.total_pages > 1 && (
        <div className="bg-white rounded-lg shadow px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Мэдээлэл */}
            <div className="text-sm text-gray-600">
              Нийт <strong>{pagination.total_items}</strong> дуудлага | 
              Хуудас <strong>{pagination.current_page}</strong> / <strong>{pagination.total_pages}</strong>
            </div>
            
            {/* Pagination товчнууд */}
            <div className="flex items-center gap-2">
              {/* Өмнөх */}
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className={`px-3 py-2 rounded flex items-center gap-1 ${
                  pagination.current_page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <FaChevronLeft className="text-xs" />
                Өмнөх
              </button>

              {/* Хуудасны дугаарууд */}
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.current_page >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded min-w-[40px] ${
                        pagination.current_page === pageNum
                          ? 'bg-blue-600 text-white font-bold'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Дараах */}
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
                className={`px-3 py-2 rounded flex items-center gap-1 ${
                  pagination.current_page === pagination.total_pages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Дараах
                <FaChevronRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content max-w-3xl">
            <div className="modal-header-enhanced">
              <h3 className="text-xl font-bold text-gray-800">
                Ачаа дуудлагын дэлгэрэнгүй
              </h3>
              <button 
                onClick={() => setShowDetailModal(false)} 
                className="icon-button"
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-6">
                {/* Header Info */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-blue-700">Дуудлагын код</span>
                    <span className="text-lg font-bold text-blue-900">{selectedRequest.request_code}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-blue-700">Төлөв</span>
                    <span className={getStatusBadge(selectedRequest.status).class}>
                      {getStatusBadge(selectedRequest.status).text}
                    </span>
                  </div>
                </div>

                {/* Илгээгчийн мэдээлэл */}
                <div>
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                    <FaUser className="mr-2 text-blue-600" />
                    Илгээгчийн мэдээлэл
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Нэр</label>
                      <p className="font-semibold">{selectedRequest.sender_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Утасны дугаар</label>
                      <p className="font-semibold">{selectedRequest.sender_phone}</p>
                    </div>
                    {selectedRequest.sender_phone_alt && (
                      <div>
                        <label className="text-sm text-gray-600">Нэмэлт утас</label>
                        <p className="font-semibold">{selectedRequest.sender_phone_alt}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ачааны мэдээлэл */}
                <div>
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                    <FaBoxOpen className="mr-2 text-green-600" />
                    Ачааны мэдээлэл
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedRequest.cargo_name && (
                      <div className="col-span-2">
                        <label className="text-sm text-gray-600">Ачааны нэр</label>
                        <p className="font-semibold">{selectedRequest.cargo_name}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm text-gray-600">Хэмжих төрөл</label>
                      <p className="font-semibold">{getMeasurementLabel(selectedRequest.measurement_type)}</p>
                    </div>
                    {selectedRequest.measurement_type === 'weight' ? (
                      <div>
                        <label className="text-sm text-gray-600">Жин</label>
                        <p className="font-semibold flex items-center">
                          <FaWeight className="mr-2 text-gray-500" />
                          {selectedRequest.weight_kg} кг
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="text-sm text-gray-600">Хэмжээ (см)</label>
                          <p className="font-semibold">
                            {selectedRequest.length_cm} × {selectedRequest.width_cm} × {selectedRequest.height_cm}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Эзэлхүүн</label>
                          <p className="font-semibold flex items-center">
                            <FaCube className="mr-2 text-gray-500" />
                            {selectedRequest.volume_cbm} м³
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Зургууд */}
                {selectedRequest.cargo_images && selectedRequest.cargo_images.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                      <FaImage className="mr-2 text-purple-600" />
                      Ачааны зургууд
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                      {selectedRequest.cargo_images.map((img, idx) => (
                        <img
                          key={idx}
                          src={`http://localhost:5000${img}`}
                          alt={`Cargo ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Тайлбар */}
                {selectedRequest.notes && (
                  <div>
                    <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                      <FaFileAlt className="mr-2 text-orange-600" />
                      Нэмэлт тайлбар
                    </h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{selectedRequest.notes}</p>
                  </div>
                )}

                {/* Төлөв өөрчлөх */}
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Төлөв өөрчлөх</h4>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleStatusChange(selectedRequest.id, 'pending')}
                      disabled={selectedRequest.status === 'pending'}
                      className="custom-button-secondary"
                    >
                      Хүлээгдэж буй
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedRequest.id, 'contacted')}
                      disabled={selectedRequest.status === 'contacted'}
                      className="custom-button"
                    >
                      Холбогдсон
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedRequest.id, 'processed')}
                      disabled={selectedRequest.status === 'processed'}
                      className="custom-button-success"
                    >
                      Боловсруулсан
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedRequest.id, 'cancelled')}
                      disabled={selectedRequest.status === 'cancelled'}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Цуцлах
                    </button>
                  </div>
                </div>

                {/* Meta info */}
                <div className="pt-4 border-t text-sm text-gray-600">
                  <div className="flex items-center gap-2 mb-1">
                    <FaClock />
                    <span>Үүсгэсэн: {new Date(selectedRequest.created_at).toLocaleString('mn-MN')}</span>
                  </div>
                  {selectedRequest.contacted_at && (
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="text-green-600" />
                      <span>Холбогдсон: {new Date(selectedRequest.contacted_at).toLocaleString('mn-MN')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {/* 🆕 Устгах товч (зөвхөн system_admin) */}
              <button
                onClick={() => handleDelete(selectedRequest.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <FaTrash />
                Устгах
              </button>
              
              <button
                onClick={() => setShowDetailModal(false)}
                className="custom-button-secondary ml-auto"
              >
                Хаах
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargoRequestsManagement;